import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiConsumer } from '../../service/api-consumer';
import { CashRegisterDetailsRequest } from '../../interface/request/CashRegisterDetailsRequest';
import { CashRegisterDetailsResponse } from '../../interface/response/CashRegisterDetailsResponse';

/**
 * Shape of the Redux state for cash register details.
 *
 * @interface DataState
 * @property {CashRegisterDetailsResponse} data - The last loaded cash register details.
 * @property {boolean} loading - Indicates whether the request is currently in progress.
 * @property {unknown | null} error - Contains an error object if the last request failed.
 *
 * Redux slice responsible for:
 * - Fetching detailed cash register data via `fetchCashRegisterDetails`.
 * - Storing loading, error, and data state.
 *
 * Contains:
 * - Extra reducers for handling the lifecycle of `fetchCashRegisterDetails` thunk:
 *   - pending: sets `loading` to true and clears previous errors.
 *   - fulfilled: updates `data` with the fetched payload and sets `loading` to false.
 *   - rejected: sets `error` with the error object and sets `loading` to false.
 *
 * Redux slice for managing cash register details state.
 *
 * @constant cashRegisterDetailsSlice
 *
 * @remarks
 * - Uses `createSlice` from Redux Toolkit.
 * - Does not have synchronous reducers; all state changes are handled via `extraReducers`.
 * - `fetchCashRegisterDetails` is expected to be a thunk action that fetches the data from an API.
 *
 * @example
 * // Dispatch the thunk to fetch details for a specific cash register
 * dispatch(fetchCashRegisterDetails({ id: 123 }));
 */

export const fetchCashRegisterDetails = createAsyncThunk(
  'data/fetchCashRegisterDetails',
  async (payload: CashRegisterDetailsRequest) => {
    const response = await ApiConsumer.getCashRegisterDetails(payload);
    return response;
  },
);


interface DataState {
  data: CashRegisterDetailsResponse;
  loading: boolean;
  error: null | unknown;
}

const initialState: DataState = {
  data: {} as CashRegisterDetailsResponse,
  loading: false,
  error: null,
};

const cashRegisterDetailsSlice = createSlice({
  name: 'cashRegisterDetails',
  initialState,
  reducers: {
    resetCashRegisterDetails: (state) => {
      state.data = {} as CashRegisterDetailsResponse;
      state.loading = true; // odmah stavi loader
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(fetchCashRegisterDetails.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(fetchCashRegisterDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    })
    .addCase(fetchCashRegisterDetails.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
  },
});

export const { resetCashRegisterDetails } = cashRegisterDetailsSlice.actions;
export default cashRegisterDetailsSlice.reducer;
